import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import CurationPanel from '../components/CurationPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = {
  1: { label: 'T1 — Mainstream', color: 'bg-emerald-100 text-emerald-800' },
  2: { label: 'T2 — Emerging', color: 'bg-blue-100 text-blue-800' },
  3: { label: 'T3 — Offbeat', color: 'bg-amber-100 text-amber-800' },
  4: { label: 'T4 — Frontier', color: 'bg-rose-100 text-rose-800' },
};

export default function AdminReview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'valleys' ? 'valleys' : 'queue';
  const deepLinkGhId = searchParams.get('gh_id');

  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);
  const [stays, setStays] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [ingestReport, setIngestReport] = useState(null);
  const [pipelineReport, setPipelineReport] = useState(null);
  const [stageFilter, setStageFilter] = useState('pending_review');
  const [selected, setSelected] = useState(null);
  const [showCsvDrawer, setShowCsvDrawer] = useState(false);
  const [topTab, setTopTab] = useState(initialTab); // 'queue' | 'valleys'
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [stageCounts, setStageCounts] = useState({ pending_review: 0, in_curation: 0, ai_drafted: 0, active: 0 });
  const [stageStale, setStageStale] = useState({ pending_review: 0, in_curation: 0, ai_drafted: 0, active: 0 });
  const [qualityFilter, setQualityFilter] = useState('all'); // all | incomplete | no_whatsapp | no_price | no_photos

  // Valleys tab state
  const [valleysData, setValleysData] = useState(null);
  const [activeStateSlug, setActiveStateSlug] = useState(null);

  const fetchPending = async (status = stageFilter) => {    setBusy(true);
    try {
      if (status === 'pending_review') {
        const r = await axios.get(`${API}/api/admin/scraper/review/list`, { params: { password } });
        setStays(r.data.stays || []);
      } else {
        const r = await axios.get(`${API}/api/admin/curation/list`, { params: { password, status, limit: status === 'active' ? 500 : 100 } });
        setStays(r.data.stays || []);
      }
      setBulkSelected(new Set());
      setAuthed(true);
      setError('');
      // Refresh stage counts so the pipeline stepper stays accurate
      try {
        const c = await axios.get(`${API}/api/admin/curation/stage-counts`, { params: { password } });
        if (c.data?.counts) setStageCounts(c.data.counts);
        if (c.data?.stale) setStageStale(c.data.stale);
      } catch (_) { /* counts are non-critical */ }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load');
    } finally {
      setBusy(false);
    }
  };

  const fetchValleys = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/scraper/preview/valleys-states`, { params: { password } });
      setValleysData(r.data);
      setActiveStateSlug(r.data.states[0]?.slug || null);
      setAuthed(true);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load valleys');
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = async () => {
    if (topTab === 'valleys') await fetchValleys();
    else await fetchPending();
  };

  // When switching to valleys tab post-auth, fetch on first open
  useEffect(() => {
    if (authed && topTab === 'valleys' && !valleysData) {
      fetchValleys();
    }
    const params = {};
    if (topTab === 'valleys') params.tab = 'valleys';
    if (deepLinkGhId) params.gh_id = deepLinkGhId;
    setSearchParams(params);
    // eslint-disable-next-line
  }, [topTab, authed]);

  // Deep-link from /admin/outreach: ?gh_id=GH-XXX opens that stay directly.
  // Resolves the stay's curation_status via the curation endpoint so we land
  // on the right stage filter, then scrolls + selects the card.
  const [deepLinkResolved, setDeepLinkResolved] = useState(false);
  const [sopOpen, setSopOpen] = useState(() => {
    try { return localStorage.getItem('admin_review_sop_collapsed') !== '1'; } catch (_) { return true; }
  });
  const toggleSop = () => {
    setSopOpen(prev => {
      try { localStorage.setItem('admin_review_sop_collapsed', prev ? '1' : '0'); } catch (_) { /* localStorage unavailable */ }
      return !prev;
    });
  };
  useEffect(() => {
    if (!authed || !deepLinkGhId || deepLinkResolved) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/admin/curation/${encodeURIComponent(deepLinkGhId)}`, { params: { password } });
        if (cancelled) return;
        const stay = r.data?.stay;
        if (!stay) { setDeepLinkResolved(true); return; }
        const targetStage = stay.curation_status || 'pending_review';
        if (targetStage !== stageFilter) {
          setStageFilter(targetStage);
          await fetchPending(targetStage);
        }
        setSelected(stay.display_name);
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="pending-stay-${stay.gohimalayan_id}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 250);
      } catch (e) {
        setError(`Deep-link: ${e.response?.data?.detail || 'stay not found'}`);
      } finally {
        if (!cancelled) setDeepLinkResolved(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [authed, deepLinkGhId]);

  const switchStage = (next) => {
    setStageFilter(next);
    if (authed) fetchPending(next);
  };

  const runAudit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/scraper/audit/run`, null, { params: { password, limit: 10 } });
      await fetchPending();
    } catch (e) { setError(e.response?.data?.detail || 'Audit failed'); }
    finally { setBusy(false); }
  };

  const approve = async (display_name) => {
    if (!window.confirm(`Approve "${display_name}" and publish?`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/scraper/review/approve`, null, { params: { password, display_name } });
      await fetchPending();
    } catch (e) { setError(e.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };

  const reject = async (display_name) => {
    if (!window.confirm(`Reject "${display_name}"? It will not be published.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/scraper/review/reject`, null, { params: { password, display_name } });
      await fetchPending();
    } catch (e) { setError(e.response?.data?.detail || 'Reject failed'); }
    finally { setBusy(false); }
  };

  const bulkApprove = async () => {
    const names = Array.from(bulkSelected);
    if (names.length === 0) return;
    if (!window.confirm(`Approve ${names.length} stays and publish them all?`)) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/scraper/review/bulk-approve`,
        { display_names: names },
        { params: { password } }
      );
      await fetchPending();
      setError('');
      alert(`Approved ${r.data.approved}/${r.data.requested} stays.`);
    } catch (e) { setError(e.response?.data?.detail || 'Bulk approve failed'); }
    finally { setBusy(false); }
  };

  const bulkReject = async () => {
    const names = Array.from(bulkSelected);
    if (names.length === 0) return;
    if (!window.confirm(`Reject ${names.length} stays? They will not be published.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/scraper/review/bulk-reject`,
        { display_names: names },
        { params: { password } }
      );
      await fetchPending();
      setError('');
      alert(`Rejected ${r.data.rejected}/${r.data.requested} stays.`);
    } catch (e) { setError(e.response?.data?.detail || 'Bulk reject failed'); }
    finally { setBusy(false); }
  };

  const toggleBulk = (name) => {
    const next = new Set(bulkSelected);
    if (next.has(name)) next.delete(name); else next.add(name);
    setBulkSelected(next);
  };

  const selectAllVisible = () => {
    setBulkSelected(new Set(stays.map(s => s.display_name)));
  };

  const clearBulk = () => setBulkSelected(new Set());

  const stripImage = async (display_name, image_url) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/scraper/review/strip-bad-image`, null, { params: { password, display_name, image_url } });
      await fetchPending();
    } catch (e) { setError(e.response?.data?.detail || 'Strip failed'); }
    finally { setBusy(false); }
  };

  const uploadCsv = async (dryRun) => {
    if (!csvFile) return;
    setBusy(true);
    setIngestReport(null);
    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      const r = await axios.post(
        `${API}/api/admin/scraper/ingest/csv`,
        fd,
        { params: { password, dry_run: dryRun }, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setIngestReport(r.data);
      if (!dryRun) await fetchPending();
    } catch (e) { setError(e.response?.data?.detail || 'Ingest failed'); }
    finally { setBusy(false); }
  };

  const runPipeline = async () => {
    if (!window.confirm('Run master data pipeline?\n\nSeeds + transform + publish (scrapers skipped to avoid timeouts). Takes ~30s.')) return;
    setBusy(true);
    setPipelineReport(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/scraper/run-all-pipeline`,
        null,
        { params: { password, skip_scrapers: true, use_llm: false }, timeout: 110000 }
      );
      setPipelineReport(r.data.report);
      await fetchPending();
    } catch (e) { setError(e.response?.data?.detail || 'Pipeline failed'); }
    finally { setBusy(false); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8">
          <h1 className="text-xl font-serif text-stone-900 mb-1">Admin Workspace</h1>
          <p className="text-sm text-stone-500 mb-6">Review queue · Valleys · CSV ingest</p>
          <input
            type="password"
            placeholder="Admin password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            className="w-full px-4 py-2 border border-stone-300 rounded mb-3"
            data-testid="review-password-input"
          />
          {error && <p className="text-sm text-rose-600 mb-3">{error}</p>}
          <button
            onClick={handleLogin}
            disabled={busy}
            className="w-full bg-stone-900 text-white py-2 rounded hover:bg-stone-800 disabled:opacity-50"
            data-testid="review-login-btn"
          >
            {busy ? 'Loading…' : 'Open workspace'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Top tabs + actions */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <h1 className="text-base font-serif text-stone-900">Admin</h1>
            <div className="flex gap-1" data-testid="top-tabs">
              <button
                onClick={() => setTopTab('queue')}
                className={`px-3 py-1.5 text-xs rounded ${topTab === 'queue' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
                data-testid="top-tab-queue"
              >
                Review Queue {topTab === 'queue' && stays.length > 0 && <span className="ml-1 text-stone-300">·{stays.length}</span>}
              </button>
              <button
                onClick={() => setTopTab('valleys')}
                className={`px-3 py-1.5 text-xs rounded ${topTab === 'valleys' ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'}`}
                data-testid="top-tab-valleys"
              >
                Valleys & States
              </button>
            </div>
          </div>

          {topTab === 'queue' && (
            <div className="hidden" data-testid="stage-filter" />
          )}

          {topTab === 'queue' && (
            <div className="flex gap-2 flex-wrap">
              <button onClick={runPipeline} disabled={busy} className="px-3 py-1.5 text-sm bg-stone-900 text-white rounded hover:bg-stone-800 disabled:opacity-50" data-testid="run-pipeline-btn">
                {busy ? 'Running…' : 'Run Pipeline'}
              </button>
              <button onClick={runAudit} disabled={busy} className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-50" data-testid="run-audit-btn">
                {busy ? 'Auditing…' : 'Run Audit (10)'}
              </button>
              <button onClick={() => fetchPending()} disabled={busy} className="px-3 py-1.5 text-sm bg-stone-100 text-stone-700 rounded hover:bg-stone-200">
                Refresh
              </button>
            </div>
          )}
        </div>
        {pipelineReport && topTab === 'queue' && (
          <div className="max-w-7xl mx-auto px-6 pb-3 text-[11px] text-stone-600 flex flex-wrap gap-x-4 gap-y-1 border-t border-stone-100 pt-2" data-testid="pipeline-report">
            <span>Seeds: <b className="text-stone-900">{pipelineReport.seeds?.ran?.length || 0}</b> ran</span>
            <span>Transform: <b className="text-stone-900">{pipelineReport.transform?.transformed_count ?? 0}</b></span>
            <span>Published: <b className="text-stone-900">{pipelineReport.publish?.published ?? 0}</b></span>
            <span>Active stays: <b className="text-emerald-700">{pipelineReport.publish?.total_active_stays ?? 0}</b></span>
            <span>Orphans cleaned: <b className="text-stone-900">{pipelineReport.publish?.orphaned_cleaned ?? 0}</b></span>
            <span>Valleys matched: <b className="text-stone-900">{pipelineReport.valley_backfill?.matched ?? 0}/{pipelineReport.valley_backfill?.total ?? 0}</b></span>
          </div>
        )}
      </div>

      {/* Pipeline stepper — replaces the old stage tabs + SOP banner.
          Shows the full 4-stage flow visually with counts, action hints, and
          a clearly highlighted CURRENT stage so anyone on the team knows where they are. */}
      {topTab === 'queue' && (
        <div className="bg-amber-50/60 border-b border-amber-200" data-testid="pipeline-stepper">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] tracking-[0.3em] uppercase text-amber-900 font-semibold inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-600" /> Curation pipeline
              </span>
              <button onClick={toggleSop} className="text-[10px] text-amber-700 hover:underline" data-testid="sop-toggle">
                {sopOpen ? 'hide details' : 'show details'}
              </button>
            </div>
            <div className="flex items-stretch gap-1 overflow-x-auto pb-1">
              {(() => {
                const stages = [
                  { k: 'pending_review', n: 1, title: 'Submitted',  action: 'Fill missing host fields → click Approve' },
                  { k: 'in_curation',    n: 2, title: 'In Curation',action: 'Drop photos into sections + scrape socials' },
                  { k: 'ai_drafted',     n: 3, title: 'AI Drafted', action: 'Review AI text → click Publish' },
                  { k: 'active',         n: 4, title: 'Live',       action: 'Already published — refine anytime' },
                ];
                // "Start here →" target: stage with the most stale items (>2 days untouched),
                // excluding Active. Falls back to the stage with the most items, then Submitted.
                const candidates = stages.slice(0, 3);
                const byStale = [...candidates].sort((a, b) => (stageStale[b.k] || 0) - (stageStale[a.k] || 0));
                let startKey = byStale[0] && stageStale[byStale[0].k] > 0 ? byStale[0].k : null;
                if (!startKey) {
                  const byCount = [...candidates].sort((a, b) => (stageCounts[b.k] || 0) - (stageCounts[a.k] || 0));
                  startKey = byCount[0] && stageCounts[byCount[0].k] > 0 ? byCount[0].k : null;
                }
                return stages.map((s, i, arr) => {
                  const isActive = stageFilter === s.k;
                  const isStart = startKey === s.k && !isActive;
                  const count = stageCounts[s.k] ?? 0;
                  const stale = stageStale[s.k] ?? 0;
                  return (
                    <React.Fragment key={s.k}>
                      <div className="flex-1 min-w-[180px] relative">
                        {isStart && (
                          <span
                            className="absolute -top-2 left-3 z-10 inline-flex items-center gap-1 bg-emerald-600 text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full shadow animate-pulse"
                            data-testid="start-here-pulse"
                            title={stale > 0 ? `${stale} stay${stale === 1 ? '' : 's'} untouched for >2 days` : `${count} waiting`}
                          >
                            ● Start here
                          </span>
                        )}
                        <button
                          onClick={() => switchStage(s.k)}
                          className={`w-full text-left rounded-md px-3 py-2 transition border-2 ${
                            isActive
                              ? 'bg-amber-700 border-amber-700 text-white shadow-md'
                              : isStart
                                ? 'bg-white border-emerald-500 text-stone-800 ring-2 ring-emerald-200'
                                : 'bg-white border-amber-200 hover:border-amber-400 text-stone-800'
                          }`}
                          data-testid={`stage-tab-${s.k}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[11px] font-bold ${
                              isActive ? 'bg-white text-amber-800' : 'bg-amber-700 text-white'
                            }`}>{s.n}</span>
                            <span className={`text-[13px] font-semibold ${isActive ? 'text-white' : 'text-stone-900'}`}>{s.title}</span>
                            <span className={`ml-auto text-[11px] px-1.5 py-0.5 rounded ${
                              isActive ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-900'
                            }`}>{count}</span>
                          </div>
                          {sopOpen && (
                            <div className={`mt-1 text-[10.5px] leading-snug ${isActive ? 'text-white/90' : 'text-stone-600'}`}>
                              {s.action}
                              {stale > 0 && !isActive && (
                                <span className="block mt-0.5 text-[10px] text-rose-600 font-medium">⚠ {stale} untouched &gt;2 days</span>
                              )}
                            </div>
                          )}
                        </button>
                      </div>
                      {i < arr.length - 1 && (
                        <div className="self-center text-amber-300 text-lg leading-none">›</div>
                      )}
                    </React.Fragment>
                  );
                });
              })()}
            </div>
            {sopOpen && (
              <div className="mt-2.5 text-[11px] text-amber-900 bg-white/60 rounded px-3 py-1.5 inline-block">
                <b>Currently viewing:</b> stage <b>{stageFilter.replace('_', ' ')}</b>. The action above tells you what to do for each card in this list.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk action bar — only on queue tab, pending_review stage, with selections */}
      {topTab === 'queue' && stageFilter === 'pending_review' && bulkSelected.size > 0 && (        <div className="bg-stone-900 text-white sticky top-[57px] z-10 shadow-lg" data-testid="bulk-action-bar">
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between flex-wrap gap-3">
            <span className="text-xs">
              <b>{bulkSelected.size}</b> selected · <button onClick={selectAllVisible} className="underline hover:text-amber-300">select all visible</button> · <button onClick={clearBulk} className="underline hover:text-amber-300">clear</button>
            </span>
            <div className="flex gap-2">
              <button onClick={bulkApprove} disabled={busy} className="px-3 py-1 text-xs bg-emerald-500 hover:bg-emerald-600 rounded disabled:opacity-50" data-testid="bulk-approve-btn">
                ✓ Approve {bulkSelected.size}
              </button>
              <button onClick={bulkReject} disabled={busy} className="px-3 py-1 text-xs bg-rose-500 hover:bg-rose-600 rounded disabled:opacity-50" data-testid="bulk-reject-btn">
                ✗ Reject {bulkSelected.size}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="max-w-7xl mx-auto px-6 py-2">
          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded p-2">{error}</div>
        </div>
      )}

      {/* QUEUE TAB */}
      {topTab === 'queue' && (
        <div className="max-w-[1600px] mx-auto px-4 py-4 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 h-[calc(100vh-100px)]">
          {/* LEFT — list of stays */}
          <main className="overflow-y-auto space-y-3 pr-1">
            <details className="bg-white rounded-lg border border-stone-200 p-3" open={showCsvDrawer} onToggle={(e) => setShowCsvDrawer(e.target.open)}>
              <summary className="cursor-pointer text-[10px] tracking-[0.2em] uppercase text-stone-500 hover:text-stone-900">CSV Ingest</summary>
              <div className="mt-3">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => { setCsvFile(e.target.files[0]); setIngestReport(null); }}
                  className="w-full text-xs mb-2"
                  data-testid="csv-file-input"
                />
                <div className="flex gap-2 mb-2">
                  <button onClick={() => uploadCsv(true)} disabled={!csvFile || busy} className="flex-1 px-2 py-1 text-xs border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50" data-testid="csv-dryrun-btn">Dry-run</button>
                  <button onClick={() => uploadCsv(false)} disabled={!csvFile || busy} className="flex-1 px-2 py-1 text-xs bg-stone-900 text-white rounded hover:bg-stone-800 disabled:opacity-50" data-testid="csv-ingest-btn">Ingest</button>
                </div>
                <p className="text-[10px] text-stone-500">Required: display_name, valley_slug, whatsapp_number, image_urls (pipe-sep).</p>
                {ingestReport && (
                  <div className="mt-2 pt-2 border-t border-stone-100 text-[11px]">
                    <p className="text-stone-600">Rows: {ingestReport.total_rows}</p>
                    <p className="text-emerald-700">✓ Accepted: {ingestReport.accepted?.length || 0}</p>
                    <p className="text-rose-700">✗ Rejected: {ingestReport.rejected?.length || 0}</p>
                  </div>
                )}
              </div>
            </details>

            {stays.length === 0 && (
              <div className="bg-white rounded-lg border border-dashed border-stone-200 p-8 text-center">
                <p className="text-sm text-stone-500">No stays in <b>{stageFilter.replace('_', ' ')}</b>.</p>
                <p className="text-[11px] text-stone-400 mt-1">{stageFilter === 'pending_review' ? 'Ingest a CSV or wait for /apply submissions.' : 'Move stays here from another stage.'}</p>
              </div>
            )}
            {/* Quality audit bar — shown only on the Live stage where stays accumulate gaps over time */}
            {topTab === 'queue' && stageFilter === 'active' && stays.length > 0 && (() => {
              const has = (s, f) => {
                if (f === 'whatsapp_number') return !!(s.whatsapp_number || '').toString().trim();
                if (f === 'starting_price_inr') return !!s.starting_price_inr;
                if (f === 'host_name') return !!(s.host_name || '').trim();
                if (f === 'photos') return (s.images || []).length > 0;
                if (f === 'hero') return (s.images || []).some(i => i.section === 'hero');
                if (f === 'storyline') return !!(s.storyline || '').trim();
                return true;
              };
              const counts = {
                no_whatsapp: stays.filter(s => !has(s, 'whatsapp_number')).length,
                no_price:    stays.filter(s => !has(s, 'starting_price_inr')).length,
                no_hero:     stays.filter(s => !has(s, 'hero')).length,
                no_storyline:stays.filter(s => !has(s, 'storyline')).length,
                incomplete:  stays.filter(s => (s.completeness_pct ?? 100) < 95).length,
              };
              return (
                <div className="bg-rose-50/50 border border-rose-200 rounded-lg p-3 mb-3" data-testid="quality-audit-bar">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-rose-900 font-semibold">⚠ Quality audit · live stays with gaps</span>
                    <span className="text-[10px] text-stone-500">Click a chip to filter</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { k: 'all',          label: 'All',            n: stays.length, cls: 'bg-stone-700 text-white' },
                      { k: 'incomplete',   label: 'Incomplete <95%',n: counts.incomplete,   cls: 'bg-amber-100 text-amber-900' },
                      { k: 'no_whatsapp',  label: 'No WhatsApp',    n: counts.no_whatsapp,  cls: 'bg-rose-100 text-rose-900' },
                      { k: 'no_price',     label: 'No price',       n: counts.no_price,     cls: 'bg-rose-100 text-rose-900' },
                      { k: 'no_hero',      label: 'No hero photo',  n: counts.no_hero,      cls: 'bg-rose-100 text-rose-900' },
                      { k: 'no_storyline', label: 'No storyline',   n: counts.no_storyline, cls: 'bg-rose-100 text-rose-900' },
                    ].map(c => (
                      <button
                        key={c.k}
                        onClick={() => setQualityFilter(c.k)}
                        className={`text-[11px] px-2 py-1 rounded inline-flex items-center gap-1.5 ${qualityFilter === c.k ? 'ring-2 ring-stone-900 ' : ''}${c.cls}`}
                        data-testid={`quality-chip-${c.k}`}
                      >
                        {c.label} <span className={`text-[10px] px-1 rounded ${qualityFilter === c.k ? 'bg-white/30' : 'bg-white/60'}`}>{c.n}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
            {stays
              .filter((s) => {
                if (stageFilter !== 'active' || qualityFilter === 'all') return true;
                if (qualityFilter === 'incomplete')   return (s.completeness_pct ?? 100) < 95;
                if (qualityFilter === 'no_whatsapp')  return !(s.whatsapp_number || '').toString().trim();
                if (qualityFilter === 'no_price')     return !s.starting_price_inr;
                if (qualityFilter === 'no_hero')      return !(s.images || []).some(i => i.section === 'hero');
                if (qualityFilter === 'no_storyline') return !(s.storyline || '').trim();
                return true;
              })
              .map((s) => {
              const summary = s.audit_summary || {};
              const failedUrls = new Set((summary.failed_images || []).map(f => f.url));
              const isSelected = selected === s.display_name;
              const isBulkSelected = bulkSelected.has(s.display_name);
              const imgCount = (s.images || []).length;
              // Slug derivation MUST match `toFeaturedSlug` in FeaturedStay.js.
              // booking_source on scraped tier is the aggregator brand (hushstays,
              // seclude, banjara, etc.) and is shared across many stays — using it
              // produces 404s. For partner tier the booking_source IS a unique slug.
              const nameSlug = (s.display_name || '').split(',')[0].trim().toLowerCase()
                .replace(/['']/g, '').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
              const liveUrl = s.partnership_status === 'partner'
                ? `/collective/${s.booking_source || nameSlug}`
                : `/stay/${nameSlug}`;
              return (
                <article
                  key={s.display_name}
                  className={`bg-white rounded-lg border-2 p-3 cursor-pointer transition ${isSelected ? 'border-blue-500 shadow-md' : isBulkSelected ? 'border-emerald-400' : 'border-stone-200 hover:border-stone-400'}`}
                  onClick={() => setSelected(s.display_name)}
                  data-testid={`pending-stay-${s.gohimalayan_id || s.display_name.replace(/\s/g,'-')}`}
                >
                  <div className="flex items-start gap-2 mb-2">
                    {stageFilter === 'pending_review' && (
                      <input
                        type="checkbox"
                        checked={isBulkSelected}
                        onChange={(e) => { e.stopPropagation(); toggleBulk(s.display_name); }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 cursor-pointer"
                        data-testid={`bulk-check-${s.gohimalayan_id || s.display_name.replace(/\s/g,'-')}`}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-serif text-stone-900 leading-tight truncate">{s.display_name}</h3>
                      <p className="text-[10px] text-stone-500 truncate">{s.valley || s.valley_slug} · {imgCount} imgs · {s.host_name || s.partnership_status || s.curation_status}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {typeof s.completeness_pct === 'number' && (
                        <span
                          className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${
                            s.completeness_pct >= 95 ? 'bg-emerald-100 text-emerald-800'
                            : s.completeness_pct >= 70 ? 'bg-amber-100 text-amber-800'
                            : 'bg-rose-100 text-rose-800'
                          }`}
                          title={
                            (s.missing_fields || []).length
                              ? `Missing: ${(s.missing_fields || []).join(', ')}`
                              : 'All required fields present'
                          }
                          data-testid={`completeness-${s.gohimalayan_id || s.display_name.replace(/\s/g,'-')}`}
                        >
                          {s.completeness_pct}%{(s.missing_fields || []).length > 0 ? ' · incomplete' : ' ✓'}
                        </span>
                      )}
                      {summary.audit_pct_passed !== undefined && (
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${summary.failed > 0 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          audit {summary.audit_pct_passed}%
                        </span>
                      )}
                    </div>
                  </div>

                  {(s.missing_fields || []).length > 0 && (s.completeness_pct ?? 100) < 95 && (
                    <p className="text-[10px] text-rose-700 mb-2 leading-tight" data-testid={`missing-${s.gohimalayan_id || s.display_name.replace(/\s/g,'-')}`}>
                      <b>Needs:</b> {(s.missing_fields || []).join(', ')}
                    </p>
                  )}

                  {imgCount > 0 && (
                    <div className="grid grid-cols-4 gap-1 mb-2">
                      {(s.images || []).slice(0, 4).map((img, i) => {
                        const url = typeof img === 'string' ? img : img.url;
                        const flags = (img.audit_flags || []);
                        const failed = flags.length > 0 || failedUrls.has(url);
                        return (
                          <div key={i} className={`relative aspect-square rounded overflow-hidden ${failed ? 'ring-1 ring-rose-400' : ''}`}>
                            <img src={url} alt="" className="w-full h-full object-cover" />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {(s.ingest_warnings || []).length > 0 && (
                    <div className="text-[10px] text-amber-700 mb-2">⚠ {s.ingest_warnings.join(' · ')}</div>
                  )}

                  <div className="flex gap-1 flex-wrap">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelected(s.display_name); }}
                      className="px-2 py-0.5 text-[10px] bg-blue-600 text-white rounded hover:bg-blue-700"
                      data-testid={`curate-${s.gohimalayan_id || s.display_name.replace(/\s/g,'-')}`}
                    >
                      Curate
                    </button>
                    {(s.curation_status === 'active' || stageFilter !== 'pending_review') && (
                      <a
                        href={liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="px-2 py-0.5 text-[10px] bg-stone-100 text-stone-700 rounded hover:bg-stone-200"
                        data-testid={`view-live-card-${s.gohimalayan_id || s.display_name.replace(/\s/g,'-')}`}
                      >
                        View live →
                      </a>
                    )}
                    {stageFilter === 'pending_review' && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); approve(s.display_name); }}
                          disabled={busy}
                          className="px-2 py-0.5 text-[10px] bg-emerald-100 text-emerald-800 rounded hover:bg-emerald-200 disabled:opacity-50"
                          data-testid={`approve-${s.gohimalayan_id}`}
                          title="Quick-publish without curation"
                        >
                          Quick approve
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); reject(s.display_name); }}
                          disabled={busy}
                          className="px-2 py-0.5 text-[10px] bg-rose-100 text-rose-700 rounded hover:bg-rose-200 disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </main>

          {/* RIGHT — curation panel */}
          <section className="bg-white rounded-lg border border-stone-200 overflow-hidden h-full" data-testid="curation-pane">
            {selected ? (
              <CurationPanel
                displayName={selected}
                password={password}
                onClose={() => setSelected(null)}
                onUpdated={() => fetchPending()}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-center px-8">
                <div>
                  <p className="text-stone-300 text-6xl font-serif mb-3">⌖</p>
                  <p className="text-sm text-stone-700 font-medium">Select a stay to curate</p>
                  <p className="text-xs text-stone-500 mt-2 max-w-xs">
                    Click any card on the left to open the editorial workspace. Add social links, scrape photos, run AI draft, and publish — all without leaving this page.
                  </p>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* VALLEYS TAB */}
      {topTab === 'valleys' && valleysData && (
        <ValleysTab data={valleysData} activeStateSlug={activeStateSlug} setActiveStateSlug={setActiveStateSlug} />
      )}
      {topTab === 'valleys' && !valleysData && (
        <div className="max-w-7xl mx-auto px-6 py-16 text-center text-sm text-stone-500" data-testid="valleys-loading">
          Loading valleys data…
        </div>
      )}
    </div>
  );
}

// Inline valleys-states preview (was /admin/data)
function ValleysTab({ data, activeStateSlug, setActiveStateSlug }) {
  const valleysForState = data.valleys.filter((v) => v.state_slug === activeStateSlug);
  const activeState = data.states.find((s) => s.slug === activeStateSlug);

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6 pb-4 border-b border-stone-200">
        <div>
          <h2 className="text-lg font-serif text-stone-900">Valleys & States — Internal Preview</h2>
          <p className="text-xs text-stone-500">Phase 1 master collection · read-only · not user-facing</p>
        </div>
        <div className="flex gap-4 text-xs text-stone-600">
          <span><b className="text-stone-900">{data.totals.states}</b> states</span>
          <span><b className="text-stone-900">{data.totals.valleys}</b> valleys</span>
          <span><b className="text-stone-900">{data.totals.valleys_with_intro}</b> w/ intro</span>
          <span><b className="text-stone-900">{data.totals.valleys_with_hero}</b> w/ hero</span>
          <span><b className="text-stone-900">{data.totals.total_stays_mapped}</b> stays mapped</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="bg-white rounded-lg border border-stone-200 p-3 h-fit lg:sticky lg:top-24" data-testid="admin-states-sidebar">
          <p className="text-[10px] tracking-[0.2em] uppercase text-stone-400 mb-2 px-2">States</p>
          {data.states.map((s) => {
            const valleyCount = data.valleys.filter((v) => v.state_slug === s.slug).length;
            const stayCount = data.valleys.filter((v) => v.state_slug === s.slug).reduce((acc, v) => acc + (v.stay_count || 0), 0);
            return (
              <button
                key={s.slug}
                onClick={() => setActiveStateSlug(s.slug)}
                className={`w-full text-left px-3 py-2 rounded mb-1 transition ${activeStateSlug === s.slug ? 'bg-stone-900 text-white' : 'hover:bg-stone-100 text-stone-700'}`}
                data-testid={`state-tab-${s.slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm">{s.short_name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${activeStateSlug === s.slug ? 'bg-white/20' : 'bg-stone-200'}`}>T{s.tier}</span>
                </div>
                <div className={`text-[10px] mt-0.5 ${activeStateSlug === s.slug ? 'text-white/70' : 'text-stone-400'}`}>
                  {valleyCount} valleys · {stayCount} stays
                </div>
              </button>
            );
          })}
        </aside>

        <main className="space-y-6">
          {activeState && (
            <section className="bg-white rounded-lg border border-stone-200 p-6" data-testid="admin-state-detail">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                <h3 className="text-2xl font-serif text-stone-900">{activeState.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded ${TIER_LABELS[activeState.tier]?.color}`}>
                  {TIER_LABELS[activeState.tier]?.label}
                </span>
              </div>
              {activeState.editorial_intro ? (
                <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-line" data-testid="state-intro">{activeState.editorial_intro}</p>
              ) : (
                <p className="text-sm text-stone-400 italic">No editorial intro yet.</p>
              )}
              <div className="mt-4 pt-4 border-t border-stone-100 text-xs text-stone-500">
                <span className="font-medium text-stone-700">Anchor valleys: </span>
                {activeState.anchor_valleys.join(' · ')}
              </div>
            </section>
          )}

          <section data-testid="admin-valleys-list">
            <p className="text-[10px] tracking-[0.2em] uppercase text-stone-400 mb-3">
              {valleysForState.length} valleys · {activeState?.name}
            </p>
            <div className="space-y-4">
              {valleysForState.map((v) => (
                <article key={v.slug} className="bg-white rounded-lg border border-stone-200 overflow-hidden grid grid-cols-1 md:grid-cols-[200px_1fr]" data-testid={`valley-card-${v.slug}`}>
                  <div className="bg-stone-100 h-40 md:h-auto relative">
                    {v.hero_image ? (
                      <img src={v.hero_image} alt={v.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-stone-400 italic p-4 text-center">no hero yet<br/>(no inventory)</div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                      <h4 className="text-lg font-serif text-stone-900">{v.name}</h4>
                      <div className="flex gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${TIER_LABELS[v.tier]?.color}`}>T{v.tier}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">{v.stay_count} {v.stay_count === 1 ? 'stay' : 'stays'}</span>
                      </div>
                    </div>
                    <div className="text-[11px] text-stone-500 mb-2 flex flex-wrap gap-3">
                      <span>{v.altitude_range_m[0]}–{v.altitude_range_m[1]}m</span>
                      <span>·</span>
                      <span>airport ~{v.drive_from_nearest_airport_hours}h</span>
                      <span>·</span>
                      <span>rail ~{v.drive_from_nearest_rail_hours}h</span>
                    </div>
                    {v.editorial_intro ? (
                      <p className="text-xs text-stone-600 leading-relaxed line-clamp-4">{v.editorial_intro}</p>
                    ) : (
                      <p className="text-xs text-stone-400 italic">No editorial intro yet.</p>
                    )}
                    <div className="mt-3 pt-3 border-t border-stone-100 text-[10px] text-stone-500">
                      <span className="font-medium text-stone-600">Anchor villages: </span>
                      {v.anchor_villages.join(' · ')}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
